import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

/* Adresse locale du connecteur de carte CPS (service installé sur le poste).
   Le test consiste simplement à vérifier que le navigateur parvient à la joindre. */
const CONNECTEUR_LOCAL = "https://localhost.icanopee.net:9982/";

/* Outil de réparation servi depuis /public/outils/ */
const OUTIL_REPARATION = "/outils/reparer-connecteur-metaconnect.bat";

function chromeVersion() {
  const m = navigator.userAgent.match(/Chrom(?:e|ium)\/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

async function etatAutorisation() {
  if (!navigator.permissions || !navigator.permissions.query) return null;
  for (const name of ["local-network-access", "loopback-network"]) {
    try {
      const r = await navigator.permissions.query({ name });
      return r.state;
    } catch (e) {
      /* nom de permission non reconnu par ce navigateur */
    }
  }
  return null;
}

async function connecteurJoignable() {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), 6000);
  try {
    await fetch(CONNECTEUR_LOCAL, { mode: "no-cors", cache: "no-store", signal: ctrl.signal });
    return true;
  } catch (e) {
    return false;
  } finally {
    clearTimeout(t);
  }
}

const VIDE = { etat: "", texte: "En attente" };

const COULEURS = [
  { id: "none", titre: "Pas d'icône", sous: "Introuvable", pastille: null },
  { id: "red", titre: "Rouge", sous: "Non connecté", pastille: "#D92D20" },
  { id: "violet", titre: "Violette", sous: "Connecté", pastille: "#7B4FD6" },
];

export default function DownloadDiagnostic() {
  const [enCours, setEnCours] = useState(false);
  const [fait, setFait] = useState(false);
  const [nav, setNav] = useState(VIDE);
  const [perm, setPerm] = useState(VIDE);
  const [conn, setConn] = useState(VIDE);
  const [ok, setOk] = useState(null);
  const [couleur, setCouleur] = useState(null);

  async function lancer() {
    setEnCours(true);
    setOk(null);
    setCouleur(null);
    const attente = { etat: "", texte: "En cours…" };
    setNav(attente);
    setPerm(attente);
    setConn(attente);

    const v = chromeVersion();
    const edge = /Edg\//.test(navigator.userAgent);
    setNav(
      v
        ? {
            etat: "ok",
            texte:
              (edge ? "Microsoft Edge " : "Chrome ") +
              v +
              (v >= 142 ? " : une autorisation d'accès au réseau local est requise." : "."),
          }
        : {
            etat: "warn",
            texte: "Navigateur non reconnu : le test reste valable, mais Chrome ou Edge sont recommandés.",
          }
    );

    const joignable = await connecteurJoignable();
    const p = await etatAutorisation();
    if (p === "denied") {
      setPerm({
        etat: "ko",
        texte:
          "Refusée pour ce site. Cliquez sur l'icône à gauche de l'adresse, ouvrez Paramètres du site, puis autorisez Accès au réseau local.",
      });
    } else if (p === "granted" || joignable) {
      setPerm({ etat: "ok", texte: "Accordée." });
    } else if (p === "prompt") {
      setPerm({
        etat: "warn",
        texte: "Pas encore accordée : relancez le test et cliquez sur Autoriser si votre navigateur le demande.",
      });
    } else {
      setPerm({ etat: "ok", texte: "Non concernée par ce navigateur." });
    }

    setConn(
      joignable
        ? { etat: "ok", texte: "Le navigateur communique avec le connecteur." }
        : { etat: "ko", texte: "Le navigateur n'arrive pas à joindre le connecteur." }
    );
    setOk(joignable);
    setFait(true);
    setEnCours(false);
  }

  const lignes = [
    { n: 1, titre: "Navigateur", s: nav },
    { n: 2, titre: "Autorisation du réseau local", s: perm },
    { n: 3, titre: "Communication avec le connecteur", s: conn },
  ];

  return (
    <div className="dgn">
      <SEO
        title="Diagnostic du connecteur de carte CPS · MetaConnect"
        description="Testez en un clic si votre navigateur communique avec le connecteur de carte CPS, et corrigez le blocage en moins de deux minutes."
        canonical="/download/diagnostic"
      />
      <style>{CSS}</style>

      <section className="dgn-hero">
        <div className="dgn-wrap">
          <span className="dgn-pill">Connecteur poste de travail</span>
          <h1>Diagnostic du connecteur en un clic</h1>
          <p className="dgn-lede">
            Le connecteur est installé, mais votre application ne le détecte toujours pas ? Lancez
            le test : en quelques secondes, vous saurez si votre navigateur arrive à le joindre, et
            quoi faire sinon.
          </p>

          <div className="dgn-panel">
            <p className="dgn-note">
              Le test se fait uniquement sur votre poste. Si votre navigateur vous demande
              l'autorisation d'accéder au réseau local, cliquez sur <b>Autoriser</b>.
            </p>
            <button type="button" className="dgn-btn" onClick={lancer} disabled={enCours}>
              {enCours ? "Test en cours…" : fait ? "Relancer le test" : "Tester mon poste"}
            </button>

            <ol className="dgn-checks">
              {lignes.map((l) => (
                <li key={l.n}>
                  <span className={"dgn-dot " + l.s.etat}>
                    {l.s.etat === "ok" ? "✓" : l.s.etat === "ko" ? "✕" : l.s.etat === "warn" ? "!" : l.n}
                  </span>
                  <div>
                    <strong>{l.titre}</strong>
                    <p>{l.s.texte}</p>
                  </div>
                </li>
              ))}
            </ol>

            {ok === true && (
              <div className="dgn-banner ok">Tout fonctionne sur ce poste.</div>
            )}
            {ok === false && (
              <div className="dgn-banner ko">
                Le connecteur n'est pas joignable depuis ce navigateur. Répondez à la question
                ci-dessous.
              </div>
            )}
          </div>

          <p className="dgn-back">
            Pas encore installé ? <Link to="/download">Téléchargez le connecteur</Link>
          </p>
        </div>
      </section>

      {ok === true && (
        <section className="dgn-sec dgn-alt">
          <div className="dgn-wrap dgn-narrow">
            <div className="dgn-head">
              <h2>Votre poste est prêt</h2>
              <p>
                Le navigateur communique avec le connecteur. Si votre application ne le détecte
                toujours pas :
              </p>
            </div>
            <ol className="dgn-steps">
              <li>
                <span className="dgn-num">1</span>
                <div>
                  <h3>Autorisez le réseau local sur votre application</h3>
                  <p>
                    Dans votre application, cliquez sur l'icône à gauche de l'adresse, ouvrez{" "}
                    <b>Paramètres du site</b> et autorisez <b>Accès au réseau local</b>. Cette
                    autorisation se donne site par site.
                  </p>
                </div>
              </li>
              <li>
                <span className="dgn-num">2</span>
                <div>
                  <h3>Rouvrez votre navigateur</h3>
                  <p>
                    Fermez-le complètement, rouvrez-le, puis cliquez sur <b>Réessayer</b>.
                  </p>
                </div>
              </li>
            </ol>
          </div>
        </section>
      )}

      {ok === false && (
        <section className="dgn-sec dgn-alt">
          <div className="dgn-wrap dgn-narrow">
            <div className="dgn-head">
              <h2>Quelle est la couleur de l'icône du connecteur ?</h2>
              <p>
                Elle se trouve près de l'horloge, en bas à droite. Si vous ne la voyez pas, cliquez
                sur la petite flèche à gauche de l'horloge.
              </p>
            </div>

            <div className="dgn-choices">
              {COULEURS.map((c) => (
                <button
                  type="button"
                  key={c.id}
                  className={"dgn-choice" + (couleur === c.id ? " on" : "")}
                  aria-pressed={couleur === c.id}
                  onClick={() => setCouleur(c.id)}
                >
                  {c.pastille && <span className="dgn-sw" style={{ background: c.pastille }} />}
                  <strong>{c.titre}</strong>
                  <span className="dgn-sub">{c.sous}</span>
                </button>
              ))}
            </div>

            {couleur === "none" && (
              <ol className="dgn-steps">
                <li>
                  <span className="dgn-num">1</span>
                  <div>
                    <h3>Redémarrez votre poste</h3>
                    <p>Le connecteur démarre automatiquement avec votre session.</p>
                  </div>
                </li>
                <li>
                  <span className="dgn-num">2</span>
                  <div>
                    <h3>Toujours pas d'icône ?</h3>
                    <p>
                      <Link to="/download">Réinstallez le connecteur</Link>, puis revenez ici et
                      relancez le test.
                    </p>
                  </div>
                </li>
              </ol>
            )}

            {couleur === "red" && (
              <ol className="dgn-steps">
                <li>
                  <span className="dgn-num">1</span>
                  <div>
                    <h3>Vérifiez votre connexion internet</h3>
                    <p>Le connecteur a besoin d'internet pour se connecter au service.</p>
                  </div>
                </li>
                <li>
                  <span className="dgn-num">2</span>
                  <div>
                    <h3>Redémarrez le service</h3>
                    <p>
                      Cliquez sur l'icône, puis dans la fenêtre qui s'ouvre, sur <b>Arrêter</b> et
                      ensuite <b>Démarrer</b>.
                    </p>
                  </div>
                </li>
                <li>
                  <span className="dgn-num">3</span>
                  <div>
                    <h3>Réseau géré par un service informatique ?</h3>
                    <p>
                      Transmettez-lui <Link to="/download/verification">la page de vérification</Link>.
                    </p>
                  </div>
                </li>
              </ol>
            )}

            {couleur === "violet" && (
              <>
                <div className="dgn-callout">
                  <h3>Le connecteur fonctionne, mais votre navigateur ne le trouve pas</h3>
                  <p>
                    C'est le cas le plus fréquent : la protection de votre box ou de votre antivirus
                    empêche le navigateur de retrouver le connecteur sur votre propre poste. Notre
                    outil le corrige en une minute.
                  </p>
                  <a className="dgn-btn" href={OUTIL_REPARATION} download>
                    Télécharger l'outil de réparation
                  </a>
                  <p className="dgn-small">
                    Windows 10/11. L'outil n'installe rien et n'ouvre aucun port : il indique
                    simplement à votre poste où trouver le connecteur, puis vérifie qu'il répond.
                  </p>
                </div>
                <ol className="dgn-steps">
                  <li>
                    <span className="dgn-num">1</span>
                    <div>
                      <h3>Ouvrez le fichier téléchargé</h3>
                      <p>
                        Acceptez la demande d'autorisation de Windows. Si Windows affiche « Windows
                        a protégé votre ordinateur », cliquez sur <b>Informations complémentaires</b>{" "}
                        puis <b>Exécuter quand même</b>.
                      </p>
                    </div>
                  </li>
                  <li>
                    <span className="dgn-num">2</span>
                    <div>
                      <h3>Laissez l'outil terminer</h3>
                      <p>
                        Si votre antivirus signale une modification de la configuration réseau,
                        autorisez-la : c'est l'objet de la correction. Attendez le message « Tout
                        est en ordre ».
                      </p>
                    </div>
                  </li>
                  <li>
                    <span className="dgn-num">3</span>
                    <div>
                      <h3>Rouvrez votre navigateur et relancez le test</h3>
                      <p>
                        Fermez complètement votre navigateur, rouvrez-le, revenez sur cette page et
                        cliquez sur <b>Relancer le test</b>.
                      </p>
                    </div>
                  </li>
                </ol>
              </>
            )}

            <div className="dgn-links">
              <span>Toujours bloqué ?</span>
              <Link to="/support-distant">Nous prenons la main sur votre poste</Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const CSS = `
.dgn{
  --ground:#F5F5F5;--surface:#fff;--line:#E6E8EC;--text:#0F172A;--muted:#5A6478;
  --blue:#0080FF;--bsoft:#EAF4FF;--bline:#CFE4FF;
  --ok:#12805C;--oksoft:#E8F6F0;--ko:#B42318;--kosoft:#FDECEA;--warn:#A15C00;--warnsoft:#FFF4E0;
  background:var(--ground);color:var(--text);
  font-family:"Instrument Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;
  -webkit-font-smoothing:antialiased;
}
.dgn *{box-sizing:border-box}
.dgn h1,.dgn h2,.dgn h3{margin:0;color:var(--text);font-weight:600;text-wrap:balance}
.dgn h1{font-size:clamp(29px,3.6vw,40px);line-height:1.14;letter-spacing:-.02em}
.dgn p{margin:0;color:var(--muted);line-height:1.6}
.dgn ul,.dgn ol{list-style:none;margin:0;padding:0}
.dgn-wrap{max-width:1000px;margin:0 auto;padding:0 28px}
.dgn-narrow{max-width:760px}

.dgn-pill{display:inline-flex;border-radius:999px;background:#E7ECF3;color:#45526B;
  font-size:11.5px;font-weight:600;letter-spacing:.11em;text-transform:uppercase;padding:7px 15px}
.dgn-hero{padding:82px 0 46px;text-align:center}
.dgn-hero h1{margin:16px auto 0;max-width:20ch}
.dgn .dgn-lede{max-width:64ch;margin:16px auto 0;font-size:16.5px}

.dgn-panel{margin:34px auto 0;background:#fff;border:1px solid var(--line);border-radius:18px;
  padding:26px 28px 22px;max-width:640px;text-align:left;box-shadow:0 18px 44px -34px rgba(15,23,42,.5)}
.dgn .dgn-note{font-size:14px;margin-bottom:16px}
.dgn-note b,.dgn-steps b,.dgn-back b{color:var(--text);font-weight:600}

.dgn-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;border:0;border-radius:999px;
  background:var(--blue);color:#fff;font:inherit;font-weight:600;font-size:15px;text-decoration:none;
  padding:13px 24px;cursor:pointer;transition:background .18s}
.dgn-btn:hover{background:#0069D1;color:#fff}
.dgn-btn:disabled{opacity:.65;cursor:wait}

.dgn-checks{margin-top:18px}
.dgn-checks li{display:flex;gap:14px;align-items:flex-start;padding:12px 0;border-top:1px solid var(--line)}
.dgn-checks li:first-child{border-top:0}
.dgn-checks strong{font-size:14.5px;font-weight:600}
.dgn-checks p{font-size:13.8px;margin-top:2px}
.dgn-dot{flex:0 0 26px;width:26px;height:26px;border-radius:50%;display:grid;place-items:center;
  font-size:12.5px;font-weight:700;background:#EEF1F5;color:var(--muted)}
.dgn-dot.ok{background:var(--oksoft);color:var(--ok)}
.dgn-dot.ko{background:var(--kosoft);color:var(--ko)}
.dgn-dot.warn{background:var(--warnsoft);color:var(--warn)}

.dgn-banner{margin-top:14px;border-radius:12px;padding:14px 16px;font-size:14.5px;font-weight:600}
.dgn-banner.ok{background:var(--oksoft);color:var(--ok)}
.dgn-banner.ko{background:var(--kosoft);color:var(--ko)}

.dgn .dgn-back{max-width:56ch;margin:22px auto 0;font-size:14.4px}
.dgn-back a,.dgn-steps a{color:#0B62C4;font-weight:600;text-decoration:none}
.dgn-back a:hover,.dgn-steps a:hover{text-decoration:underline}

.dgn-sec{padding:56px 0}
.dgn-alt{background:#fff;border-top:1px solid var(--line);border-bottom:1px solid var(--line)}
.dgn-head{text-align:center;max-width:64ch;margin:0 auto}
.dgn-head h2{font-size:clamp(22px,2.6vw,30px);line-height:1.2;letter-spacing:-.018em}
.dgn-head p{margin-top:12px;font-size:15.5px}

.dgn-choices{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:28px}
.dgn-choice{display:flex;flex-direction:column;align-items:flex-start;gap:4px;text-align:left;
  background:var(--ground);border:1px solid var(--line);border-radius:14px;padding:18px 18px;
  font:inherit;color:var(--text);cursor:pointer;transition:border-color .18s,box-shadow .18s}
.dgn-choice:hover{border-color:var(--bline)}
.dgn-choice.on{border-color:var(--blue);background:#fff;box-shadow:0 0 0 3px var(--bsoft)}
.dgn-choice strong{font-size:15.5px;font-weight:600}
.dgn-sub{font-size:13px;color:var(--muted)}
.dgn-sw{width:16px;height:16px;border-radius:50%;margin-bottom:4px}

.dgn-callout{margin-top:26px;background:var(--bsoft);border:1px solid var(--bline);border-radius:14px;padding:22px 22px 20px}
.dgn-callout h3{font-size:17px;line-height:1.3;margin-bottom:8px}
.dgn-callout p{font-size:14.2px}
.dgn-callout .dgn-btn{margin-top:16px}
.dgn .dgn-small{font-size:12.8px;margin-top:12px}

.dgn-steps{display:grid;gap:12px;margin-top:22px}
.dgn-steps li{display:flex;gap:16px;align-items:flex-start;background:var(--ground);border:1px solid var(--line);
  border-radius:14px;padding:20px 22px}
.dgn-steps h3{font-size:15.5px;line-height:1.3;margin-bottom:6px}
.dgn-steps p{font-size:14px}
.dgn-num{flex:0 0 30px;width:30px;height:30px;border-radius:50%;background:var(--bsoft);
  border:1px solid var(--bline);color:#0B62C4;font-weight:600;font-size:14px;display:grid;place-items:center}

.dgn-links{display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap;
  margin-top:26px;background:var(--bsoft);border:1px solid var(--bline);border-radius:14px;padding:18px 22px}
.dgn-links span{color:#3D4759;font-size:14px}
.dgn-links a{display:inline-flex;align-items:center;background:#fff;border:1px solid var(--bline);
  border-radius:999px;padding:9px 18px;color:#0B62C4;font-weight:600;font-size:14px;text-decoration:none}
.dgn-links a:hover{background:#DBEBFF}

@media (max-width:700px){
  .dgn-wrap{padding:0 16px}
  .dgn-hero{padding:56px 0 36px}
  .dgn-panel{padding:20px 18px}
  .dgn-choices{grid-template-columns:1fr}
}
`;
